import { createContext, useContext, useEffect, useState } from 'react';

import {
  INITIAL_PAGE_NUMBER,
  MAX_PAGE_SIZE,
  columnFilterOptions,
  emptyTableState,
  tableHeaderDisplayName,
} from '../../components/Tickets/MyTickets.constants';
import {
  generateFilterQuery,
  generateSortQuery,
} from '../../components/Tickets/utils/query';
import axios from 'axios';
import {
  SingleTicketInitial,
  SingleTicketTransformed,
  FilterOption,
  FilterOptions,
  SortOrder,
} from 'src/components/Tickets/models/Tickets.interface';
import {
  ColumnId,
  ServiceDeskContextState,
  ServiceDeskProviderProps,
} from './ServiceDeskContext.types';

const ServiceDeskContext = createContext(
  null as null | ServiceDeskContextState
);

export const ServiceDeskProvider = ({ children }: ServiceDeskProviderProps) => {
  const [addTicketPopup, setAddTicketPopup] = useState<boolean>(false);
  const [ticketsDataTableAll, setTicketsDataTableAll] = useState<
    Partial<SingleTicketTransformed>[] | null
  >(null);
  const [ticketsDataModified, setTicketsDataModified] = useState<
    Partial<SingleTicketTransformed>[] | null
  >(null);
  const [ticketDataModifiedInfinite, setTicketDataModifiedInfinite] = useState<{
    [key: number]: Partial<SingleTicketTransformed>[];
  } | null>(null);
  const [loadingTickets, setLoadingTickets] = useState<boolean>(false);
  const [openedCurrentDropdownColumnId, setOpenedCurrentDropdownColumnId] =
    useState<ColumnId>(null);
  const [sortedColumnId, setSortedColumnId] = useState<ColumnId>(null);
  const [sortedOrder, setSortedOrder] = useState<SortOrder | null>(null);
  const [isSelectedFilter, setIsSelectedFilter] = useState<boolean>(false);
  const [filterOptions, setFilterOptions] =
    useState<FilterOptions>(columnFilterOptions);
  const [isTicketSubmitted, setIsTicketSubmitted] = useState<boolean>(false);

  const [currentPageNumber, setCurrentPageNumber] =
    useState<number>(INITIAL_PAGE_NUMBER);
  const [totalPagesNumber, setTotalPagesNumber] = useState<number | null>(null);
  const [pageLinks, setPageLinks] = useState<{
    [key in number]: string | null;
  }>({ 1: null });
  const [isReached, setIsReached] = useState<{ [key in number]: boolean }>({
    1: false,
  });
  const [isLoadingInfinite, setIsLoadingInfinite] = useState<boolean>(false);
  // Ticket Annotations/Notes
  const [viewTicketDetailsPopup, setViewTicketDetailsPopup] =
    useState<boolean>(false);
  const [viewTicketId, setViewTicketId] = useState<string | number>('');
  const [ticketOwner, setTicketOwner] = useState<string>('');
  const [openAttachOverlay, setOpenAttachOverlay] = useState<boolean>(false);
  const [fileUploaded, setFileUploaded] = useState<boolean>(false);
  const [uploadedFile, setUploadedFile] = useState<File>();
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showInputField, setShowInputField] = useState<boolean>(false);
  const [isMobileSortOpen, setIsMobileSortOpen] = useState<boolean>(false);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState<boolean>(false);

  const toggleTicketDetails = (
    ticketId: string | number,
    owner: string,
    toggle?: boolean
  ) => {
    if (!toggle) {
      setViewTicketDetailsPopup(false);
      return;
    }
    setViewTicketDetailsPopup(true);
    setViewTicketId(ticketId);
    setTicketOwner(owner);
  };

  const toggleAttachOverlay = (toggle: boolean) => {
    setOpenAttachOverlay(toggle);
  };

  const handleUploadedFile = (file: File, isFileUploaded: boolean) => {
    setUploadedFile(file);
    setFileUploaded(isFileUploaded);
  };

  const handleShowErrorMessage = (
    showMessage: boolean,
    messageText: string
  ) => {
    setShowErrorMessage(showMessage);
    setErrorMessage(messageText);
  };

  const toggleInputField = (toggle: boolean) => {
    setShowInputField(toggle);
  };

  // ADD NEW TICKET POPUP
  const toggleAddTicketPopup = (toggle?: boolean) => {
    if (toggle) {
      setAddTicketPopup(true);
    } else {
      setAddTicketPopup(false);
    }
  };
  // DESKTOP DROPDOWN
  const toggleDesktopTableDropdown = (
    columnId?: ColumnId,
    toggle?: boolean
  ) => {
    if (toggle) {
      setOpenedCurrentDropdownColumnId(columnId);
    } else {
      setOpenedCurrentDropdownColumnId(null);
    }
  };

  // PAGES
  const handleNextPageNumber = () => {
    if (currentPageNumber < totalPagesNumber) {
      const nextPageNumber = currentPageNumber + 1;
      setCurrentPageNumber(nextPageNumber);
    }
  };

  const handlePrevPageNumber = () => {
    if (currentPageNumber > 1) {
      const prevPageNumber = currentPageNumber - 1;
      setCurrentPageNumber(prevPageNumber);
    }
  };

  const resetCurrentPageNumber = () => {
    setCurrentPageNumber((prev) => (prev !== 1 ? INITIAL_PAGE_NUMBER : prev));
    setIsReached({ 1: false });
  };

  const handleFilterChecked = () => {
    const isAtLeastOneFilterChecked = Object.values(filterOptions).some(
      (filter) => filter.some((data) => data.checked)
    );

    setIsSelectedFilter(isAtLeastOneFilterChecked);
  };

  // REQUESTS
  const getEndpoint = () => {
    const sortQuery = generateSortQuery(sortedColumnId, sortedOrder);
    const filterQuery = generateFilterQuery(filterOptions);

    const root = `api/tickets?maxPageSize=${MAX_PAGE_SIZE}`;
    const query = [];

    if (sortQuery) {
      query.push(sortQuery);
    }
    if (filterQuery) {
      query.push(filterQuery);
    }
    return query.length > 0 ? `${root}&${query.join('&')}` : root;
  };

  const getTicketsData = async (pageLinkParam?: string) => {
    handleFilterChecked();

    try {
      if (currentPageNumber === 1) {
        setLoadingTickets(true);
      }

      const endpoint = getEndpoint();

      const { data } = (await axios.get(endpoint, {
        params: {
          nextPage: pageLinkParam && pageLinkParam,
        },
      })) as {
        data: {
          tickets: SingleTicketInitial[];
          nextPage: string | undefined;
          count: number | null;
        };
      };

      if (data) {
        if (data?.tickets?.length === 0) {
          setTicketsDataTableAll([]);
          setTicketsDataModified([]);
          return;
        }
        const formattedData = data?.tickets?.map((data) => {
          return {
            ...data,
            owner: data?.owner?.fullname,
          };
        }) as Partial<SingleTicketTransformed>[];

        if (data?.nextPage) {
          const encodedUrl = data?.nextPage;
          const decodedUrl = decodeURIComponent(encodedUrl);
          const totalPages = Math.ceil(data?.count / MAX_PAGE_SIZE);
          setTotalPagesNumber(totalPages);
          setPageLinks((prev) => ({
            ...prev,
            [currentPageNumber + 1]: decodedUrl,
          }));
        }

        if (currentPageNumber === 1) {
          setTicketDataModifiedInfinite({ [currentPageNumber]: formattedData });
        } else if (currentPageNumber > 1) {
          setTicketDataModifiedInfinite((prev) => ({
            ...prev,
            [currentPageNumber]: formattedData,
          }));
        }

        setTicketsDataTableAll(formattedData);
        setTicketsDataModified(formattedData);
      }
    } catch {
      setTicketsDataTableAll(null);
      setTicketsDataModified(null);
    } finally {
      setLoadingTickets(false);
      setIsLoadingInfinite(false);
    }
  };

  // SORT
  const sortTickets = (sortOrder: SortOrder, columnId: ColumnId) => {
    const colId = columnId === 'owner' ? '_ownerid_value' : columnId;
    setSortedOrder(sortOrder);
    setSortedColumnId(colId);
    toggleDesktopTableDropdown();
    resetCurrentPageNumber();
  };

  // FILTER
  const filterTicketsBySingleOption = (columnId: ColumnId, option: string) => {
    // Update the filter options for the currently opened dropdown
    const currentFilter = filterOptions[columnId]?.map((filter) => {
      if (filter.label === option) {
        filter.checked = !filter.checked;
      }

      return filter;
    });

    setFilterOptions((prev) => ({
      ...prev,
      [openedCurrentDropdownColumnId]: currentFilter,
    }));
    resetCurrentPageNumber();
  };

  const filterTicketsByMultipleOptions = (flt: FilterOptions) => {
    setFilterOptions((prev) => {
      return Object.entries(prev).reduce((acc, [colId, colFiltersArray]) => {
        const id = colId as keyof FilterOptions;
        const filterOptions = colFiltersArray as FilterOption[];

        return {
          ...acc,
          [id]: filterOptions?.map((data, i) => ({
            ...data,
            checked: flt[id][i].checked,
          })),
        };
      }, {} as FilterOptions);
    });
    resetCurrentPageNumber();
  };

  const deleteSingleFilter = (columnId: ColumnId, option: string) => {
    const currentFilter = filterOptions[columnId]?.map((filter) => {
      if (filter.label === option) {
        filter.checked = false;
      }

      return filter;
    });

    setFilterOptions((prev) => ({
      ...prev,
      [columnId]: currentFilter,
    }));
    resetCurrentPageNumber();
  };

  const deleteAllFilters = () => {
    setFilterOptions((prev) => {
      return Object.entries(prev).reduce((acc, [colId, colFiltersArray]) => {
        const id = colId as keyof FilterOptions;
        const filterOptions = colFiltersArray as FilterOption[];
        return {
          ...acc,
          [id]: filterOptions?.map((data) => ({
            ...data,
            checked: false,
          })),
        };
      }, {} as FilterOptions);
    });
    resetCurrentPageNumber();
  };

  const handleSubmittedTicket = () => {
    setIsTicketSubmitted(true);
    resetCurrentPageNumber();
  };
  // MOBILE SORT AND FILTER MODALS
  const mobileSortToggle = (toggle?: boolean) => {
    if (toggle) {
      setIsMobileSortOpen(true);
    } else {
      setIsMobileSortOpen(false);
    }
  };

  const mobileFilterToggle = (toggle?: boolean) => {
    if (toggle) {
      setIsMobileFilterOpen(true);
    } else {
      setIsMobileFilterOpen(false);
    }
  };

  useEffect(() => {
    if (isTicketSubmitted) {
      getTicketsData();
      setIsTicketSubmitted(false);
    }
  }, [isTicketSubmitted]);

  useEffect(() => {
    getTicketsData(pageLinks[currentPageNumber]);
  }, [currentPageNumber, filterOptions, sortedOrder, sortedColumnId]);

  return (
    <ServiceDeskContext.Provider
      value={{
        // TABLE DISPLAY NAMES
        tableHeaderDisplayNameLookUp: tableHeaderDisplayName,

        // TICKETS
        ticketsDataTableAll,
        ticketsDataModified,
        ticketDataModifiedInfinite,
        loadingTickets,

        // DESKTOP DROPDOWN
        openedCurrentDropdownColumnId,
        toggleDesktopTableDropdown,

        // SORT
        sortedOrder,
        sortedColumnId,
        sortTickets,
        isMobileSortOpen,
        mobileSortToggle,

        // FILTER
        isSelectedFilter,
        filterOptions,
        filterTicketsBySingleOption,
        filterTicketsByMultipleOptions,
        deleteSingleFilter,
        deleteAllFilters,
        handleSubmittedTicket,
        isMobileFilterOpen,
        mobileFilterToggle,

        // PAGES
        currentPageNumber,
        totalPagesNumber,
        handleNextPageNumber,
        handlePrevPageNumber,
        isReached,
        setIsReached,
        isLoadingInfinite,
        setIsLoadingInfinite,

        // EMPTY STATE
        emptyState: emptyTableState,

        // ADD NEW TICKET POPUP
        addTicketPopup,
        toggleAddTicketPopup,

        // TICKET ANNOTATIONS/NOTES
        viewTicketDetailsPopup,
        viewTicketId,
        ticketOwner,
        toggleTicketDetails,
        openAttachOverlay,
        toggleAttachOverlay,
        fileUploaded,
        uploadedFile,
        handleUploadedFile,
        showErrorMessage,
        errorMessage,
        handleShowErrorMessage,
        showInputField,
        toggleInputField,
      }}
    >
      {children}
    </ServiceDeskContext.Provider>
  );
};

export const useServiceDeskContext = () => {
  const context = useContext(ServiceDeskContext);
  if (context === undefined) {
    throw new Error(
      'useServiceDeskContext must be used within a ServiceDeskProvider'
    );
  }
  return context;
};
